import { WarningBanner } from 'components/ui/Banner';
import { API_RATE_LIMIT } from 'constants/general';
import { ROUTES, WEBVIEW_ROUTES } from 'constants/routes';
import { useEffect, useMemo, useState } from 'react';
import { resetApiRateLimitState } from 'services/axios-config';
import { t } from 'services/i18n.service';
import { useAppSelectorShallow } from 'store/hooks';
import { apiRateLimitSelector } from 'store/reducer/general/selector';
import {
  formatApiRateLimitRemainingSeconds,
  getApiRateLimitRemainingMs,
  isApiRateLimited
} from 'utils/api-rate-limit.util';
import { isCurrentPathIn } from 'utils/routes.util';

const ApiRateLimitBanner = () => {
  const { apiRateLimitedUntil } = useAppSelectorShallow(apiRateLimitSelector);
  const isUserSettingsModified = useAppSelectorShallow(
    (state) => state.setting.isUserSettingsModified
  );
  const [, tick] = useState(0);

  const isBlocked = isApiRateLimited(apiRateLimitedUntil);
  const remainingMs = getApiRateLimitRemainingMs(apiRateLimitedUntil);
  const remainingSeconds = formatApiRateLimitRemainingSeconds(remainingMs);

  const isOnSettingsRoute = isCurrentPathIn([
    ROUTES.SETTINGS,
    WEBVIEW_ROUTES.WEBVIEW_SETTINGS
  ]);
  const showUnsavedReloadWarning = isOnSettingsRoute && isUserSettingsModified;

  useEffect(() => {
    if (!isBlocked) {
      return;
    }

    const intervalId = window.setInterval(() => {
      tick((value) => value + 1);
    }, API_RATE_LIMIT.COUNTDOWN_TICK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isBlocked, apiRateLimitedUntil]);

  useEffect(() => {
    if (!apiRateLimitedUntil || remainingMs > 0) {
      return;
    }

    resetApiRateLimitState();
    window.location.reload();
  }, [apiRateLimitedUntil, remainingMs]);

  const message = useMemo(() => {
    if (!isBlocked) {
      return '';
    }

    return t(API_RATE_LIMIT.I18N.BANNER, {
      seconds: remainingSeconds
    });
  }, [isBlocked, remainingSeconds]);

  if (!isBlocked) {
    return null;
  }

  return (
    <div className='sticky top-0 z-50 w-full shrink-0 flex flex-col ~gap-1.5/2 ~px-2/3 ~pb-2/3'>
      <WarningBanner message={message} size='small' />
      <p className='~text-[0.625rem]/xs text-amber-900 dark:text-amber-100 ~px-1/2'>
        {t(API_RATE_LIMIT.I18N.BANNER_HINT)}
      </p>
      {showUnsavedReloadWarning && (
        <p className='~text-[0.625rem]/xs font-medium text-amber-900 dark:text-amber-100 ~px-1/2'>
          {t(API_RATE_LIMIT.I18N.RELOAD_UNSAVED_WARNING, {
            seconds: remainingSeconds
          })}
        </p>
      )}
    </div>
  );
};

export default ApiRateLimitBanner;
